import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import toast from 'react-hot-toast';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const InviteHistory: React.FC = () => {
  const { companyId, userId } = useParams<{ companyId: string; userId: string }>();
  const { t } = useTranslation('usages');
  const queryClient = useQueryClient();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  usePageTitle(t('inviteHistoryTitle') ?? 'Invite history');

  const { data, isLoading, error } = useQuery({
    queryKey: ['invitations-by-company-inviter', companyId, userId],
    queryFn: () => apiService.getInvitationsByCompany(companyId!, { inviter_id: userId! }),
    enabled: !!companyId && !!userId,
  });

  const cancelMutation = useMutation({
    mutationFn: (invitationId: string) => apiService.cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations-by-company-inviter', companyId, userId] });
      toast.success(t('inviteCancelled') ?? 'Invitation cancelled');
      setCancellingId(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error ?? (t('cancelFailed') ?? 'Failed to cancel'));
      setCancellingId(null);
    },
  });

  const invitations = data?.invitations ?? [];

  if (!companyId || !userId) {
    return (
      <div className="p-4 sm:p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('invalidLink') ?? 'Invalid link'}</p>
        <Link to="/administration/usages" className="text-indigo-600 dark:text-indigo-400 hover:underline mt-2 inline-block">
          {t('backToUsages') ?? 'Back to Usage'}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <div className="mb-6 flex items-center gap-4">
        <Link
          to={`/administration/usages/clients/${companyId}`}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          {t('backToUsage') ?? 'Back to usage'}
        </Link>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <EnvelopeIcon className="w-5 h-5 text-indigo-500" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('inviteHistory') ?? 'Invite history'}
          </h1>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-sm text-red-600 dark:text-red-400">
            {t('errorLoadingInvites') ?? 'Failed to load invites'}
          </div>
        ) : invitations.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('noInvitesForUser') ?? 'No invitations sent by this user for this company.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('inviteeEmail') ?? 'Invitee'}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('role') ?? 'Role'}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('mode') ?? 'Mode'}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('status') ?? 'Status'}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('createdAt') ?? 'Created'}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('details') ?? 'Details'}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('actions') ?? 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {invitations.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{inv.invitee_email}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{inv.role}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{inv.mode}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{inv.status}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono text-xs whitespace-nowrap">
                      {formatDateTime(inv.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Link
                        to={`/invitations/${inv.id}`}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                      >
                        {t('viewDetails') ?? 'View details'}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {inv.status === 'pending' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCancellingId(inv.id);
                            cancelMutation.mutate(inv.id);
                          }}
                          disabled={cancellingId === inv.id}
                          className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 hover:underline font-medium disabled:opacity-50"
                        >
                          <XMarkIcon className="w-4 h-4" />
                          {cancellingId === inv.id ? (t('cancelling') ?? 'Cancelling…') : (t('cancel') ?? 'Cancel')}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteHistory;
